import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import { 
  Plus, Trash2, Edit3, Search, X, Loader2, 
  ImageIcon, Upload, CheckCircle, Globe, Lock, 
  Zap, LogOut, ShieldCheck, BarChart3, ExternalLink, Send
} from 'lucide-react';
import Groq from "groq-sdk";

const rawApiKey = process.env.REACT_APP_GROQ_API_KEY;
const groq = new Groq({ 
  apiKey: rawApiKey || 'dummy_key', 
  dangerouslyAllowBrowser: true 
});

const AdminPage = () => {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [ideas, setIdeas] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('Semua');

  const [imageKeyword, setImageKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]); 

  const initialForm = {
    judul: '', tag: 'Digital', modal_min: '', modal_max: '', 
    kesulitan: 1, legalitas: 'NIB', risk_level: 'Rendah', 
    summary: '', langkah_mulai: '', rincian_modal: '', 
    img_url: '', 
    img_attribution_name: '', 
    img_attribution_link: '', 
    is_published: false 
  };
  
  const [formData, setFormData] = useState(initialForm);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchIdeas();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchIdeas();
    });
    return () => subscription.unsubscribe();
  }, []);

  // --- UPGRADED AI TREND GENERATOR LOGIC ---
  const generateAIContent = async (selectedCategory) => {
    setLoading(true);
    setShowAiModal(false);

    const existingTitles = ideas.map(i => i.judul).join(", ");

    try {
      const completion = await groq.chat.completions.create({
        messages: [
          {
            role: "system",
            content: `Anda adalah pakar UMKM Indonesia. Buatkan 1 tren ide bisnis tahun ini dengan kategori ${selectedCategory}.
            
            KRITERIA UTAMA:
            1. Modal maksimum 10 juta.
            2. Buatkan rincian modal seminimal mungkin untuk memulai usaha.
            3. Pastikan syarat lagalitas yang sesuai dengan ide bisnis.
            3. Hindari judul yang sudah ada: [${existingTitles}].

            WAJIB Berikan output dalam JSON murni dengan struktur berikut:
            {
              "judul": "Nama Bisnis Kreatif",
              "summary": "Penjelasan singkat persuasif",
              "modal_min": angka modal minimum (tanpa Rp),
              "modal_max": angka (tanpa Rp),
              "risk_level": "Rendah/Sedang/Tinggi",
              "kesulitan": 1-5 (angka),
              "legalitas": persyaratan legalitas yang dibutuhkan (jika nama legalitas panjang sebutkan singkatannya saja),
              "langkah_mulai": "Langkah 1; Langkah 2; Langkah 3; Langkah 4",
              "rincian_modal": rincian modal minimum sesuaikan totalnya dengan modal_min
            }
            
            PENTING: 
            1. Gunakan pemisah slash (/) untuk legalitas.
            2. Gunakan pemisah titik koma (;) untuk langkah_mulai dan rincian_modal.`
          },
          {
            role: "user",
            content: `Buatkan tren ide bisnis untuk kategori ${selectedCategory}.`
          }
        ],
        model: "llama-3.3-70b-versatile",
        response_format: { type: "json_object" }
      });

      const aiData = JSON.parse(completion.choices[0]?.message?.content || "{}");
      
      setFormData(prev => ({ 
        ...prev, 
        judul: aiData.judul || "",
        summary: aiData.summary || "",
        tag: selectedCategory,
        modal_min: aiData.modal_min || 0,
        modal_max: aiData.modal_max || 0,
        risk_level: aiData.risk_level || "Rendah",
        kesulitan: aiData.kesulitan || 1,
        legalitas: aiData.legalitas || "",
        langkah_mulai: aiData.langkah_mulai || "",
        rincian_modal: aiData.rincian_modal || ""
      }));

    } catch (err) {
      alert("AI Gagal: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    return {
      total: ideas.length,
      digital: ideas.filter(i => i.tag === 'Digital').length,
      kuliner: ideas.filter(i => i.tag === 'Kuliner').length,
      jasa: ideas.filter(i => i.tag === 'Jasa').length,
      agri: ideas.filter(i => i.tag === 'Agri').length,
    };
  }, [ideas]);

  const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const fetchIdeas = async () => {
    setFetchLoading(true);
    const { data, error } = await supabase.from('ideas').select('*').order('created_at', { ascending: false });
    if (!error) setIdeas(data);
    setFetchLoading(false);
  };

  const handleFileUpload = async (event) => {
    try {
      setUploading(true);
      const file = event.target.files[0];
      if (!file) return;
      const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '')}`;
      const { error: upError } = await supabase.storage.from('idea-images').upload(`uploads/${fileName}`, file);
      if (upError) throw upError;
      const { data } = supabase.storage.from('idea-images').getPublicUrl(`uploads/${fileName}`);
      
      setFormData(prev => ({ 
        ...prev, 
        img_url: data.publicUrl, 
        img_attribution_name: '', 
        img_attribution_link: '' 
      }));
      setSearchResults([]);
    } catch (err) { alert("Upload gagal: " + err.message); } finally { setUploading(false); }
  };

  const handleManualSearchImage = async () => {
    if (!imageKeyword) return alert("Ketik keyword pencarian gambar (Inggris)");
    const UNSPLASH_ACCESS_KEY = 'phqAaN7E05skLvfSYA1JtMnrdmdZqkhBftxsSYlcgtY';
    setUploading(true);
    try {
      const res = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(imageKeyword)}&per_page=12&orientation=landscape&client_id=${UNSPLASH_ACCESS_KEY}`);
      if (!res.ok) throw new Error("Limit API Unsplash mungkin tercapai");
      const data = await res.json();
      setSearchResults(data.results);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const slug = editId ? formData.slug : `${slugify(formData.judul)}-${Date.now()}`;
      const payload = {
        ...formData,
        modal_min: parseInt(formData.modal_min) || 0,
        modal_max: parseInt(formData.modal_max) || 0,
        slug: slug,
      };
      const { error } = editId 
        ? await supabase.from('ideas').update(payload).eq('id', editId)
        : await supabase.from('ideas').insert([payload]);
      if (error) throw error;
      closeModal();
      fetchIdeas();
      alert("Berhasil disimpan!");
    } catch (err) { 
      alert("Error simpan: " + err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) alert("Gagal Login: " + error.message);
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIdeas([]);
  };

  const openModal = (item = null) => {
    if (item) {
      setEditId(item.id);
      setFormData({ ...item });
    } else {
      setEditId(null);
      setFormData(initialForm);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setShowAiModal(false);
    setEditId(null);
    setFormData(initialForm);
    setImageKeyword('');
    setSearchResults([]);
  };

  const filteredIdeas = ideas.filter(item => 
    item.judul.toLowerCase().includes(searchQuery.toLowerCase()) && 
    (filterTag === 'Semua' || item.tag === filterTag)
  );

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-['Onest']">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-xl p-10 border border-slate-100">
          <div className="text-center mb-8">
            <div className="bg-emerald-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="text-emerald-600" size={32} />
            </div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tighter uppercase">Admin Portal</h2>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl outline-none border border-transparent focus:border-emerald-500/20" placeholder="Email" required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl outline-none border border-transparent focus:border-emerald-500/20" placeholder="Password" required />
            <button type="submit" disabled={authLoading} className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-black transition-all">
              {authLoading ? 'Loading...' : 'Masuk Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10 font-['Onest'] text-slate-900">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
          <div>
            <h1 className="text-4xl font-semibold text-emerald-600 tracking-tight">mikropedia <span className="text-slate-800">@admin</span></h1>
            <button onClick={handleLogout} className="text-[10px] font-black text-red-500 uppercase flex items-center gap-1 mt-1 hover:underline"><LogOut size={12}/> Logout</button>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
             <button onClick={() => openModal()} className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black transition-all hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 flex items-center gap-2">
               <Plus size={20}/> NEW IDEA
             </button>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-emerald-600 p-6 rounded-[2rem] text-white shadow-lg shadow-emerald-200 flex flex-col justify-between">
            <BarChart3 size={20} className="opacity-40 mb-2"/>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Total Ide</p>
              <h3 className="text-3xl font-black">{stats.total}</h3>
            </div>
          </div>
          {['Digital', 'Kuliner', 'Jasa', 'Agri'].map(category => (
            <div key={category} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{category}</p>
              <h3 className="text-2xl font-black text-slate-800">{stats[category.toLowerCase()]}</h3>
            </div>
          ))}
        </div>

        {/* Filter & Table */}
        <div className="bg-white p-4 rounded-3xl mb-6 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-4 justify-between items-center">
            <div className="relative w-full md:w-96">
               <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
               <input className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-2xl text-sm outline-none" placeholder="Cari ide..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <div className="flex gap-2 overflow-x-auto w-full md:w-auto">
               {['Semua', 'Digital', 'Kuliner', 'Jasa', 'Agri'].map(t => (
                 <button key={t} onClick={() => setFilterTag(t)} className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all ${filterTag === t ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-400'}`}>{t}</button>
               ))}
            </div>
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
          {fetchLoading ? (
            <div className="p-20 flex flex-col items-center gap-4">
              <Loader2 className="animate-spin text-emerald-600" size={40}/>
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Syncing Database...</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-slate-50/50 text-[11px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                <tr>
                  <th className="p-6">Content</th>
                  <th className="p-6 text-center">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredIdeas.map(item => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-all group">
                    <td className="px-6 py-4 flex items-center gap-4">
                      <img src={item.img_url} className="w-20 h-16 rounded-xl object-cover bg-slate-100 border border-slate-100 shadow-sm" alt="" />
                      <div>
                        <p className="font-bold text-slate-800 line-clamp-1">{item.judul}</p>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-tighter italic">{item.tag}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase border flex items-center justify-center gap-1.5 mx-auto w-fit ${item.is_published ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                        {item.is_published ? <Globe size={10}/> : <Lock size={10}/>} {item.is_published ? 'Live' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                        <button onClick={() => openModal(item)} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all"><Edit3 size={18}/></button>
                        <button onClick={() => {if(window.confirm("Hapus?")) supabase.from('ideas').delete().eq('id', item.id).then(fetchIdeas)}} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 size={18}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* MODAL SECTION - UPDATED WITH MORE FIELDS */}
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" onClick={closeModal}></div>
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-[3.5rem] bg-white p-8 md:p-12 shadow-2xl animate-in zoom-in-95 duration-300">
                <div className="flex justify-between items-center mb-10 pb-6 border-b border-slate-50">
                  <div>
                    <h2 className="text-2xl font-black uppercase tracking-tighter">{editId ? 'Edit Content' : 'Create New Content'}</h2>
                    {!editId && <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Gunakan AI untuk mencari ide yang sedang tren</p>}
                  </div>

                  <div className="flex items-center gap-3">
                    {!editId && (
                      <div className="relative">
                        <button 
                          type="button"
                          onClick={() => setShowAiModal(!showAiModal)}
                          disabled={loading}
                          className="bg-emerald-50 text-emerald-600 px-4 py-2 rounded-2xl font-black text-[10px] uppercase flex items-center gap-2 hover:bg-emerald-100 transition-all border border-emerald-100"
                        >
                          <Zap size={14} fill="currentColor"/> {loading ? 'Thinking...' : 'AI Trend Generator'}
                        </button>

                        {showAiModal && (
                          <div className="absolute right-0 mt-3 w-56 bg-white border border-slate-100 shadow-2xl rounded-3xl p-4 z-[120] animate-in slide-in-from-top-2 duration-200">
                             <p className="text-[9px] font-black text-slate-400 uppercase mb-3 px-2 tracking-widest">Pilih Bidang Tren:</p>
                             <div className="space-y-1">
                               {['Digital', 'Kuliner', 'Jasa', 'Agri'].map((cat) => (
                                 <button
                                   key={cat}
                                   type="button"
                                   onClick={() => generateAIContent(cat)}
                                   className="w-full text-left px-4 py-3 rounded-xl text-[11px] font-bold text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 transition-all flex justify-between items-center group"
                                 >
                                   {cat} <Plus size={14} className="opacity-0 group-hover:opacity-100 transition-all"/>
                                 </button>
                               ))}
                             </div>
                          </div>
                        )}
                      </div>
                    )}
                    <button onClick={closeModal} className="p-2 bg-slate-50 rounded-full text-slate-400 hover:bg-slate-100 transition-all"><X size={24}/></button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Visual & Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50/50 p-8 rounded-[2.5rem] border border-slate-100">
                    <div className="flex flex-col gap-4">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Media Visual</label>
                      <div className="w-full h-48 bg-white rounded-3xl border-2 border-dashed border-slate-200 overflow-hidden flex flex-col items-center justify-center relative shadow-inner group">
                        {formData.img_url ? (
                          <>
                            <img src={formData.img_url} className="w-full h-full object-cover" alt="preview" />
                            {formData.img_attribution_name && (
                              <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md px-3 py-2 rounded-xl text-[9px] text-white flex items-center gap-2 border border-white/10">
                                <ExternalLink size={12}/> By {formData.img_attribution_name}
                              </div>
                            )}
                          </>
                        ) : <ImageIcon className="text-slate-200" size={32}/>}
                        {uploading && <div className="absolute inset-0 bg-white/80 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600"/></div>}
                      </div>
                      
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          placeholder="Search Unsplash (English)..."
                          value={imageKeyword}
                          onChange={(e) => setImageKeyword(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleManualSearchImage())}
                          className="flex-1 pl-4 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-[11px] font-bold outline-none"
                        />
                        <button type="button" onClick={handleManualSearchImage} className="bg-slate-900 text-white px-4 rounded-xl text-[10px] font-black uppercase tracking-widest">Cari</button>
                      </div>

                      {searchResults.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 bg-white p-2 rounded-2xl border border-slate-200 max-h-40 overflow-y-auto">
                          {searchResults.map((img) => (
                            <button key={img.id} type="button" onClick={() => {
                              setFormData({...formData, img_url: img.urls.regular, img_attribution_name: img.user.name, img_attribution_link: img.user.links.html});
                              setSearchResults([]);
                            }} className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-emerald-500 transition-all">
                              <img src={img.urls.thumb} className="w-full h-full object-cover" alt="" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-4 block tracking-widest">Visibility Status</label>
                        <button type="button" onClick={() => setFormData({...formData, is_published: !formData.is_published})} className={`w-full py-6 rounded-[2rem] text-[11px] font-black uppercase transition-all border-4 ${formData.is_published ? 'bg-emerald-600 text-white border-emerald-100 shadow-xl' : 'bg-white text-slate-400 border-slate-100'}`}>
                          {formData.is_published ? '🚀 Live on Website' : '📁 Save as Draft'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Modal Min</label>
                          <input type="number" className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-sm font-semibold" value={formData.modal_min} onChange={e => setFormData({...formData, modal_min: e.target.value})} />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Modal Max</label>
                          <input type="number" className="w-full p-4 bg-white border border-slate-100 rounded-2xl text-sm font-semibold" value={formData.modal_max} onChange={e => setFormData({...formData, modal_max: e.target.value})} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Input Utama */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Judul Bisnis</label>
                      <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-semibold outline-none" value={formData.judul} onChange={e => setFormData({...formData, judul: e.target.value})} required />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Kategori</label>
                      <select className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-black outline-none" value={formData.tag} onChange={e => setFormData({...formData, tag: e.target.value})}>
                         <option>Digital</option><option>Kuliner</option><option>Jasa</option><option>Agri</option>
                      </select>
                    </div>

                    <div className="md:col-span-3">
                      <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Ringkasan (Summary)</label>
                      <textarea className="w-full p-5 bg-slate-50 border border-slate-100 rounded-[2rem] text-sm h-24 outline-none resize-none leading-relaxed" value={formData.summary} onChange={e => setFormData({...formData, summary: e.target.value})} />
                    </div>

                    {/* Technical Details (Penting untuk Detail Page agar tidak kosong) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:col-span-3">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Risiko</label>
                        <select className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold" value={formData.risk_level} onChange={e => setFormData({...formData, risk_level: e.target.value})}>
                          <option>Rendah</option><option>Sedang</option><option>Tinggi</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Legalitas</label>
                        <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold" value={formData.legalitas} onChange={e => setFormData({...formData, legalitas: e.target.value})} />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Kesulitan (1-5)</label>
                        <input type="number" min="1" max="5" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold" value={formData.kesulitan} onChange={e => setFormData({...formData, kesulitan: e.target.value})} />
                      </div>
                    </div>

                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Langkah Mulai (Pemisah ;)</label>
                        <textarea className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs h-32 leading-relaxed" value={formData.langkah_mulai} onChange={e => setFormData({...formData, langkah_mulai: e.target.value})} placeholder="Contoh: Riset; Cari Supplier; Promosi..." />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block tracking-wider">Rincian Modal (Pemisah ;)</label>
                        <textarea className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs h-32 leading-relaxed" value={formData.rincian_modal} onChange={e => setFormData({...formData, rincian_modal: e.target.value})} placeholder="Contoh: Alat: 2jt; Bahan: 500rb..." />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-4 pt-6 border-t border-slate-50">
                    <button type="submit" disabled={loading || uploading} className="flex-[2] bg-slate-900 text-white py-6 rounded-[2.5rem] font-black text-[12px] flex items-center justify-center gap-3 hover:bg-black transition-all shadow-xl disabled:opacity-50 uppercase tracking-widest">
                      {loading ? <Loader2 className="animate-spin" size={20}/> : <CheckCircle size={20}/>}
                      {editId ? 'Update Content' : 'Publish Content'}
                    </button>
                    <button type="button" onClick={closeModal} className="flex-1 bg-slate-100 text-slate-400 py-6 rounded-[2.5rem] font-black text-[12px] hover:bg-slate-200 uppercase transition-all">
                      Discard
                    </button>
                  </div>
                </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;